import { QueryClient } from '@tanstack/react-query'

import { ApiError } from './api/client'

/** Longest wait honoured for a rate-limited query before giving up. */
const MAX_RATE_LIMIT_WAIT_SEC = 30

/**
 * Retries server errors (5xx / network) twice. A 429 is retried once, only
 * after its Retry-After (and not at all when that is too long); other 4xx
 * never retry.
 */
export function shouldRetry(count: number, err: unknown): boolean {
  if (err instanceof ApiError && err.rateLimited) {
    return count < 1 && (err.retryAfterSec ?? MAX_RATE_LIMIT_WAIT_SEC + 1) <= MAX_RATE_LIMIT_WAIT_SEC
  }
  return !(err instanceof ApiError && err.status < 500) && count < 2
}

/** Delay before the next retry: Retry-After for 429, otherwise exponential backoff. */
export function retryDelay(count: number, err: unknown): number {
  if (err instanceof ApiError && err.rateLimited && err.retryAfterSec !== undefined) return err.retryAfterSec * 1000
  return Math.min(1000 * 2 ** count, 30_000)
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 10_000,
        retry: shouldRetry,
        retryDelay,
      },
    },
  })
}
